import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Room,
  RoomEvent,
  Track,
  createLocalTracks,
  ConnectionState,
  ConnectionQuality
} from 'livekit-client';

const DEFAULT_LIVEKIT_URL =
  process.env.REACT_APP_LIVEKIT_URL || 'ws://localhost:7880';

/**
 * Manages a single LiveKit room connection for the active call view.
 *
 * @param {Object} params
 * @param {string} params.token - LiveKit access token from POST /api/calls[/join]
 * @param {string} [params.livekitUrl] - wss:// URL of LiveKit SFU
 * @param {boolean} [params.autoConnect=true]
 */
export default function useLiveKitRoom({ token, livekitUrl, autoConnect = true }) {
  const [connectionState, setConnectionState] = useState(ConnectionState.Disconnected);
  const [localParticipant, setLocalParticipant] = useState(null);
  const [remoteParticipants, setRemoteParticipants] = useState([]);
  const [connectionQuality, setConnectionQuality] = useState(ConnectionQuality.Unknown);
  const [error, setError] = useState(null);

  const roomRef = useRef(null);

  const refreshRemotes = useCallback((room) => {
    const participants = Array.from(room.remoteParticipants.values())
      .filter(p => !p.identity?.startsWith('aimhi-transcriber-'));
    setRemoteParticipants(participants);
  }, []);

  useEffect(() => {
    if (!token || !autoConnect) return;

    const room = new Room({
      adaptiveStream: true,
      dynacast: true
    });
    roomRef.current = room;

    room.on(RoomEvent.ConnectionStateChanged, (state) => {
      setConnectionState(state);
    });
    room.on(RoomEvent.ParticipantConnected, () => refreshRemotes(room));
    room.on(RoomEvent.ParticipantDisconnected, () => refreshRemotes(room));
    room.on(RoomEvent.TrackSubscribed, () => refreshRemotes(room));
    room.on(RoomEvent.TrackUnsubscribed, () => refreshRemotes(room));
    room.on(RoomEvent.Disconnected, () => {
      setLocalParticipant(null);
      setRemoteParticipants([]);
    });
    room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
      // Track the local participant's outbound quality as the call's quality signal.
      if (participant?.isLocal) {
        setConnectionQuality(quality);
      }
    });

    (async () => {
      try {
        const url = livekitUrl || DEFAULT_LIVEKIT_URL;
        await room.connect(url, token);

        const localTracks = await createLocalTracks({
          audio: true,
          video: { resolution: { width: 1280, height: 720 } }
        });
        for (const t of localTracks) {
          await room.localParticipant.publishTrack(t);
        }
        setLocalParticipant(room.localParticipant);
        refreshRemotes(room);
      } catch (err) {
        console.error('[useLiveKitRoom] connect error:', err);
        setError(err);
      }
    })();

    return () => {
      (async () => {
        try {
          room.localParticipant?.tracks?.forEach(pub => {
            try { pub.track?.stop(); } catch (_) { /* noop */ }
          });
          await room.disconnect();
        } catch (_) { /* noop */ }
      })();
      roomRef.current = null;
    };
  }, [token, livekitUrl, autoConnect, refreshRemotes]);

  const toggleAudio = useCallback(async () => {
    const lp = roomRef.current?.localParticipant;
    if (!lp) return;
    const enabled = lp.isMicrophoneEnabled;
    await lp.setMicrophoneEnabled(!enabled);
  }, []);

  const toggleVideo = useCallback(async () => {
    const lp = roomRef.current?.localParticipant;
    if (!lp) return;
    const enabled = lp.isCameraEnabled;
    await lp.setCameraEnabled(!enabled);
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await roomRef.current?.disconnect();
    } catch (_) { /* noop */ }
  }, []);

  return {
    room: roomRef.current,
    connectionState,
    connectionQuality,
    localParticipant,
    remoteParticipants,
    error,
    toggleAudio,
    toggleVideo,
    disconnect
  };
}
